import { service } from '@ember/service';
import { attr, belongsTo, hasMany, type AsyncBelongsTo, type AsyncHasMany, type SyncHasMany } from '@ember-data/model';
import type StoreService from '@ember-data/store';
import AbstractGroupInvite from 'garaje/models/abstract/abstract-group-invite';
import type GroupInviteInstanceTemplateModel from 'garaje/models/group-invite-instance-template';
import GroupInviteLink from 'garaje/models/group-invite-link';
import type InviteModel from 'garaje/models/invite';
import type RecurringGroupInvite from 'garaje/models/recurring-group-invite';
import type UserModel from 'garaje/models/user';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type PlainObject from 'garaje/utils/plain-object';
import urlBuilder from 'garaje/utils/url-builder';
import { v4 as uuidv4 } from 'uuid';

export default class GroupInvite extends AbstractGroupInvite {
  @service declare ajaxFetch: AjaxFetchService;

  @attr('date') declare expectedArrivalTime: Date | null;
  @attr('string', { defaultValue: () => uuidv4() }) declare jobId: string;

  @belongsTo('recurring-group-invite', { async: true, inverse: null })
  declare recurringGroupInvite: AsyncBelongsTo<RecurringGroupInvite>;

  @belongsTo('user', { async: true, inverse: null }) declare creator: AsyncBelongsTo<UserModel>;
  @hasMany('invite', { async: true, inverse: 'groupInvite' }) declare invites: AsyncHasMany<InviteModel>;

  @hasMany('group-invite-instance-template', { async: false, inverse: 'groupInvite' })
  declare inviteTemplates: SyncHasMany<GroupInviteInstanceTemplateModel>;

  @hasMany('group-invite-link', { async: false, inverse: 'groupInvite' })
  declare groupInviteLinks: SyncHasMany<GroupInviteLink>;

  notifyGroupInvite(data: PlainObject<unknown> | undefined): Promise<unknown> {
    return this.ajaxFetch.request(urlBuilder.v3.groupInvites.sendNotification(this.id), {
      contentType: 'application/json',
      data,
      type: 'POST',
    });
  }

  createGroupInviteLink(): GroupInviteLink {
    const capacityLimit = this.location.capacityLimit || 25;
    const groupInviteLink = this.store.createRecord('group-invite-link', {
      groupInvite: this,
      capacityLimit,
      additionalGuests: false,
      expiresAt: GroupInviteLink.calculateDefaultExpiresAt(this.expectedArrivalTime),
    });

    return groupInviteLink;
  }

  static groupInviteIncludes(options?: {
    includeRecurringGroupInvites?: boolean;
    includeGroupInviteLinks?: boolean;
    includeInviteTemplates?: boolean;
  }): string {
    const { includeRecurringGroupInvites, includeGroupInviteLinks, includeInviteTemplates } = options ?? {};
    const baseIncludes = ['employee', 'flow', 'location', 'additional-hosts'];

    if (includeRecurringGroupInvites) baseIncludes.push('recurring-group-invite');
    if (includeGroupInviteLinks) baseIncludes.push('group-invite-links');
    if (includeInviteTemplates) baseIncludes.push('invite-templates');

    return baseIncludes.join(',');
  }

  static shouldReloadRecord(store: StoreService, groupInviteId: string): boolean {
    const groupInvite = store.peekRecord('group-invite', groupInviteId);

    if (!groupInvite) return true;

    const employeeId = groupInvite.belongsTo('employee').id();
    const flowId = groupInvite.belongsTo('flow').id();
    const locationId = groupInvite.belongsTo('location').id();

    // If there is associated data that isn't loaded, reload the group invite
    if (employeeId && !store.peekRecord('employee', employeeId)) return true;
    if (flowId && !store.peekRecord('flow', flowId)) return true;
    if (locationId && !store.peekRecord('location', locationId)) return true;

    // If groupInviteLinks relationship is not loaded, reload to fetch them
    const groupInviteLinks = groupInvite.hasMany('groupInviteLinks').value();
    if (!groupInviteLinks || groupInviteLinks.length === 0) return true;

    return false;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'group-invite': GroupInvite;
  }
}
